import { Injectable } from "@angular/core";
import * as moment from "moment/moment";
import { PlanningBlock } from "../../pb-screen/models/planning-block/planning-block.interface";

@Injectable({
  providedIn: "root"
})
export class CheckDateCompatabilityService {

  private readonly startAndEndOfTheRangeAreInclusive = "[]";

  public forPlanningOnSingleDate(planDate: moment.Moment, planningBlocks: PlanningBlock[]): boolean {
    const startOfDay = this.getStartOfDay(planDate);
    const endOfDay = this.getEndOfDay(planDate);

    let dateIsCompatible = true;

    for (const planningBlock of planningBlocks) {
      dateIsCompatible = this.checkStartFromAndUntil(planningBlock, startOfDay, endOfDay);

      // If 'StartFrom' or 'StartUntil' is during the intended plan day, then no need to check the bookedFrom and bookedUntil
      if (dateIsCompatible) {
        continue;
      }

      // If 'StartFrom' or 'StartUntil' is not during the intended plan day, then check the bookedFrom and bookedUntil
      const bookedDateTimeFromIsFilledIn = moment.utc(planningBlock.bookedDatetimeFrom).year() > 1800;
      if (bookedDateTimeFromIsFilledIn) {
        dateIsCompatible = this.checkBookedFromAndUntil(planningBlock, startOfDay, endOfDay);
      }

      // If one of the dates is not compatible, then return false aka not compatible
      if (!dateIsCompatible) {
        return false;
      }
    }

    return dateIsCompatible;
  }

  private getStartOfDay(planDate: moment.Moment) {
    return moment({ y: planDate.year(), M: planDate.month(), D: planDate.date() });
  }

  private getEndOfDay(planDate: moment.Moment) {
    return moment({ y: planDate.year(), M: planDate.month(), D: planDate.date(), h: 23, m: 59, s: 59 });
  }

  private fromIsDuringPlanDay(pbFrom: moment.Moment, startOfDay: moment.Moment, endOfDay: moment.Moment) {
    return pbFrom.isBetween(startOfDay, endOfDay,
      undefined,
      this.startAndEndOfTheRangeAreInclusive
    );
  }

  private untilIsDuringPlanDay(pbUntil: moment.Moment, startOfDay: moment.Moment, endOfDay: moment.Moment) {
    return pbUntil.isBetween(startOfDay, endOfDay,
      undefined,
      this.startAndEndOfTheRangeAreInclusive
    );
  }

  private checkBookedFromAndUntil(planningBlock: PlanningBlock, startOfDay: moment.Moment, endOfDay: moment.Moment) {
    const bookedFrom = moment.utc(planningBlock.bookedDatetimeFrom).local();
    const bookedUntil = moment.utc(planningBlock.bookedDatetimeUntil).local();

    return this.fromIsDuringPlanDay(bookedFrom, startOfDay, endOfDay)
      || this.untilIsDuringPlanDay(bookedUntil, startOfDay, endOfDay);
  }

  private checkStartFromAndUntil(planningBlock: PlanningBlock, startOfDay: moment.Moment, endOfDay: moment.Moment) {
    const pbStartFrom = moment.utc(planningBlock.startFrom).local();
    const pbStartUntil = moment.utc(planningBlock.startUntil).local();

    return this.fromIsDuringPlanDay(pbStartFrom, startOfDay, endOfDay)
      || this.untilIsDuringPlanDay(pbStartUntil, startOfDay, endOfDay);
  }
}
